import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { vars } from '../../../reactive';
import { JSONparse } from '../../../utils';
import { isEmail, isPhone } from '../../../utils/validators';
import FormChoice from '../../FormChoice/FormChoice';
import FormChoiceCheckbox from '../../FormChoiceCheckbox/FormChoiceCheckbox';
import FormInput from '../../FormInput/FormInput';
import { FORM_INPUT_TYPES, isLocaleDDMMYYYY } from '../../FormInput/types';
import FormMultiText from '../../FormMultiText/FormMultiText';
import { FORM_MULTI_TEXT_TYPES } from '../../FormMultiText/types';
import FormSelect from '../../FormSelect/FormSelect';
import FormText from '../../FormText/FormText';
import { PreviousPage } from '../../Shared/FormContainer/FormContainer';
import { CenteredLoadingSpinner } from '../../Shared/LoadingSpinner/LoadingSpinner';
import { FormRenderProps, Question, onQuestionSubmitType } from './types';

const FormQuestion = ({
  item,
  onQuestionSubmit,
  previousPage,
  loading = false,
  registerAs,
  defaultValue
}: {
  item: Question;
  onQuestionSubmit: onQuestionSubmitType;
  previousPage: PreviousPage;
  loading?: boolean;
  registerAs: string;
  defaultValue?: unknown;
}) => {
  const { formId, question, PetRecordId } = item;
  const { id, type } = question;
  const onSubmit = (answer: any) => onQuestionSubmit({ formId: formId, questionId: id, answer, petRecordId: PetRecordId });

  return (
    <>
      {loading && <CenteredLoadingSpinner />}
      {!loading && (
        <>
          {type === 'RADIO' && <FormChoiceRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={(defaultValue as string[])[0]} />}
          {type === 'CHECKBOX' && <FormChoiceCheckboxRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string[]} />}
          {type === 'TEXT' && <FormTextRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} />}
          {type === 'DATE' && <FormInputRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} />}
          {type === 'DROPDOWN' && <FormSelectRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={(defaultValue as string[])[0]} />}
          {type === 'BOOLEAN' && <FormBooleanRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={String(defaultValue as string)} />}
          {type === 'TEXTAREA' && <FormTextRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} textArea />}
          {type === 'POSTCODE' && (
            <FormTextRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} validateType={type} />
          )}
          {type === 'PHONE' && <FormPhoneRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} />}
          {type === 'EMAIL' && <FormTextRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} validateType={type} />}
          {type === 'NUMBER' && <FormTextRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} number />}
          {type === 'ADDRESS' && <FormAddressRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} />}
          {type === 'NAME_ADDRESS' && <FormNameAddressRender registerAs={registerAs} question={question} onSubmit={onSubmit} previousPage={previousPage} defaultValue={defaultValue as string} />}
        </>
      )}
    </>
  );
};

const FormChoiceRender = ({ registerAs, question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string }) => {
  const { title, AnswerOptions } = question;
  const formOptions = useForm();
  const { control } = formOptions;
  const componentItems = { title: title, options: AnswerOptions.map(answer => ({ option: answer.title, value: answer.id })), registerAs };
  const data = useWatch({ name: `choice_${registerAs}`, control });

  useEffect(() => {
    if (data) {
      onSubmit(data);
    }
  }, [data]);

  return <FormChoice formOptions={formOptions} componentItems={componentItems} previousPage={previousPage} defaultValue={defaultValue} />;
};

const FormChoiceCheckboxRender = ({ registerAs, question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string[] }) => {
  const formOptions = useForm();
  const { title, AnswerOptions } = question;
  const componentItems = { title: title, options: AnswerOptions.map(answer => ({ option: answer.title, value: answer.id })), registerAs };
  const handleSubmit = (data: any) => {
    const values = data[`choice_${registerAs}`]?.filter((value: any) => !!value);
    onSubmit(values);
  };

  return <FormChoiceCheckbox formOptions={formOptions} onSubmit={handleSubmit} componentItems={componentItems} previousPage={previousPage} defaultValue={defaultValue} />;
};

const FormTextRender = ({
  registerAs,
  question,
  onSubmit,
  previousPage,
  textArea,
  number,
  defaultValue,
  validateType
}: FormRenderProps & { defaultValue: string; textArea?: boolean; number?: boolean; validateType?: 'PHONE' | 'EMAIL' | 'POSTCODE' }) => {
  const formOptions = useForm();
  const { title } = question;
  const pattern = validateType === 'PHONE' ? isPhone : validateType === 'EMAIL' ? isEmail : number ? /^[0-9]*$/ : undefined;
  const componentItems = { title, placeholder: title, registerAs, pattern };

  const handleSubmit = (data: any) => {
    const value = data[registerAs];
    onSubmit(value);
  };

  return <FormText formOptions={formOptions} onSubmit={handleSubmit} componentItems={componentItems} previousPage={previousPage} defaultValue={defaultValue} textArea={textArea} number={number} />;
};

const FormInputRender = ({ question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string }) => {
  const type = FORM_INPUT_TYPES.DATE;
  const formOptions = useForm();
  const { title } = question;
  const defaultDate = defaultValue ? new Date(defaultValue) : '';
  const defaultValues = defaultDate
    ? [isLocaleDDMMYYYY ? defaultDate.getMonth() + 1 : defaultDate.getDate(), isLocaleDDMMYYYY ? defaultDate.getDate() : defaultDate.getMonth() + 1, defaultDate.getFullYear()]
    : undefined;

  const handleSubmit = (data: any) => {
    const date = data[type].reverse();

    const yyyy = date[0];
    const mm = isLocaleDDMMYYYY ? date[1] : date[2];
    const dd = isLocaleDDMMYYYY ? date[2] : date[1];

    const dateFixed = new Date(Date.UTC(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10)));

    onSubmit(dateFixed.toISOString());
  };

  return <FormInput type={type} formOptions={formOptions} onSubmit={handleSubmit} previousPage={previousPage} title={title} defaultValues={defaultValues} />;
};

const FormSelectRender = ({ registerAs, question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string }) => {
  const formOptions = useForm();
  const { title, AnswerOptions } = question;
  const options = AnswerOptions.map(answer => ({ name: answer.title, id: answer.id }));

  const componentItems = { title, registerAs };

  const handleSubmit = (data: any) => {
    const value = data[registerAs];
    onSubmit(value);
  };

  return <FormSelect formOptions={formOptions} onSubmit={handleSubmit} previousPage={previousPage} defaultValue={defaultValue} options={options} componentItems={componentItems} />;
};

const FormBooleanRender = ({ registerAs, question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue?: string }) => {
  const { t } = useTranslation();
  const formOptions = useForm();
  const { control } = formOptions;
  const { title } = question;
  const componentItems = {
    title: title,
    options: [
      { option: t('formQuestion.yes', 'Yes'), value: 'true' },
      { option: t('formQuestion.no', 'No'), value: 'false' }
    ],
    registerAs
  };
  const data = useWatch({ name: `choice_${registerAs}`, control });

  useEffect(() => {
    if (data) {
      onSubmit(data);
    }
  }, [data]);

  return <FormChoice formOptions={formOptions} componentItems={componentItems} previousPage={previousPage} defaultValue={defaultValue} />;
};

const FormAddressRender = ({ question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string }) => {
  const { t } = useTranslation();
  const type = FORM_MULTI_TEXT_TYPES.ADDRESS;
  const { title } = question;
  const handleSubmit = (data: any) => {
    const value = JSON.stringify([
      {
        country: vars.currentBranch()?.address?.country || t('formQuestion.defaultCountry', 'United Kingdom'),
        ...data
      }
    ]);
    onSubmit(value);
  };
  const defaultValues = JSONparse(defaultValue)?.[0];
  const formOptions = useForm({
    defaultValues: {
      ...defaultValues
    }
  });

  return <FormMultiText type={type} formOptions={formOptions} onSubmit={handleSubmit} previousPage={previousPage} defaultValue={defaultValues} title={title} />;
};

const FormPhoneRender = ({ question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string }) => {
  const type = FORM_MULTI_TEXT_TYPES.PHONE;
  const { title } = question;
  const handleSubmit = (data: any) => {
    const value = JSON.stringify(data);
    onSubmit(value);
  };

  const defaultValues = JSONparse(defaultValue);
  const formOptions = useForm({
    defaultValues: {
      ...defaultValues
    }
  });

  return <FormMultiText type={type} formOptions={formOptions} onSubmit={handleSubmit} previousPage={previousPage} defaultValue={defaultValues} title={title} />;
};

const FormNameAddressRender = ({ question, onSubmit, previousPage, defaultValue }: FormRenderProps & { defaultValue: string }) => {
  const type = FORM_MULTI_TEXT_TYPES.NAME_ADDRESS;
  const { title } = question;
  const handleSubmit = (data: any) => {
    const value = JSON.stringify(data);
    onSubmit(value);
  };

  const defaultValues = JSONparse(defaultValue);
  const formOptions = useForm({
    defaultValues: {
      ...defaultValues
    }
  });

  return <FormMultiText type={type} formOptions={formOptions} onSubmit={handleSubmit} previousPage={previousPage} defaultValue={defaultValues} title={title} />;
};

export default FormQuestion;
